import { Location } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { PromptTypes } from 'generated/graphql-types';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, switchMap, switchMapTo, tap, withLatestFrom } from 'rxjs/operators';
import { accountSettingsRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { BulkConfigureExtensionsDialogComponent } from 'src/app/common-components/bulk-configure-extensions-dialog/bulk-configure-extensions-dialog.component';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { RestApiError } from 'src/app/common-components/errors/models/error-message';
import { Privileges } from 'src/app/privileges/privileges';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { AddPromptComponent } from '../../add-prompt/containers/add-prompt.component';
import { EditPresenceCredentialsComponent } from '../../edit-presence-credentials/containers/edit-presence-credentials.component';
import { getAccountDetail } from '../queries/account-detail.graphql';
import { GetAccountDetail, GetAccountDetailVariables } from '../queries/generated/GetAccountDetail';
import { AccountDetailApiService } from '../services/account-detail-api.service';
import * as AccountDetailActions from './account-detail.actions';
import { bulkConfigureExtensions } from './account-detail.actions';

@Injectable()
export class AccountDetailEffects {
   loadAccountDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            AccountDetailActions.accountDetailLoad,
            AccountDetailActions.saveAccountDetailSuccess,
            AccountDetailActions.accountDetailCancel
         ),
         withLatestFrom(this.store.pipe(select(selectRouteParams))),
         concatMap(([action, params]) => {
            const id = parseInt(params.id, 10);

            return this.apiService
               .query<GetAccountDetail, GetAccountDetailVariables>({
                  query: getAccountDetail,
                  variables: { id },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute))
                  ),
                  map(([accountResult, privileges, route]) => {
                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageAccountSettings_View,
                           accountResult.data.account?.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return AccountDetailActions.loadAccountAccessDenied();
                     }
                     return AccountDetailActions.loadAccountDetailsSuccess({
                        data: accountResult.data.account,
                     });
                  }),
                  catchError((error) => {
                     return of(AccountDetailActions.loadAccountDetailsFailure(error));
                  })
               );
         })
      );
   });

   addPrompt$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AccountDetailActions.addMohPrompt),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddPromptComponent, {
                  data: {
                     dontIncludeRecordingOption: true,
                     includedPromptTypes: [PromptTypes.MusicOnHold],
                     onlyPromptTypeOption: PromptTypes.MusicOnHold,
                     routeToNewPromptAfterSave: false,
                     isPromptForAccountMoh: true,
                  },
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   saveAccountDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(AccountDetailActions.saveAccountDetail),
         switchMap((action) => {
            const apiCall = this.accountDetailApiService.save(action.account, action.accountFields);
            return apiCall.pipe(
               map((data) => {
                  return AccountDetailActions.saveAccountDetailSuccess({ data: data.data });
               }),
               catchError((error) => {
                  return of(AccountDetailActions.saveAccountDetailFailure(error));
               })
            );
         })
      );
   });

   navigate$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(AccountDetailActions.navigate),
            tap((action) => {
               this.router.navigateByUrl(`/${accountSettingsRoute}/${action.id}`, { replaceUrl: true });
            })
         ),
      { dispatch: false }
   );

   saveAccountDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AccountDetailActions.saveAccountDetailSuccess),
            tap(() => {
               this.snackBar.open('Account Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   bulkConfigureExtensions$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(bulkConfigureExtensions),
            tap((data) => {
               this.dialog.open(BulkConfigureExtensionsDialogComponent, {
                  panelClass: 'ndt-bulk-configure-extensions-dialog',
                  data: { accountId: data.accountId },
               });
            })
         ),
      { dispatch: false }
   );

   editPresenceCredentials$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AccountDetailActions.editPresenceCredentials),
            switchMap((action) => {
               const dialogRef = this.dialogService.openFormComponent(EditPresenceCredentialsComponent, {
                  closeOnNavigation: true,
                  disableClose: true,
                  data: {
                     username: action.username,
                     enabled: action.presenceEnabled,
                  },
                  autoFocus: false,
                  panelClass: ['dialog-md', 'dialog-container'],
               });

               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   editPresenceCredentialsSubmit$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AccountDetailActions.editPresenceCredentialsSubmit),
         withLatestFrom(this.store.pipe(select(getSelectedAccountId))),
         switchMap((action) => {
            return this.accountDetailApiService
               .savePresenceCredentials(action[1] ?? 0, action[0].username, action[0].password, action[0].fields)
               .pipe(
                  map((response: HttpResponse<number>) => {
                     this.snackBar.open(`Presence Credentials Edited`);
                     return AccountDetailActions.accountDetailLoad();
                  }),
                  catchError((error: RestApiError) => {
                     return of(AccountDetailActions.editPresenceCredentialsFailure(error));
                  })
               );
         })
      );
   });

   rebootAllPhones$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AccountDetailActions.rebootAllPhones),
         switchMap((action) => {
            const message = 'Are you sure you want to reboot all phones?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Reboot All Phones?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.accountDetailApiService.rebootAllPhones(action.accountId).pipe(
                        map(() => {
                           return AccountDetailActions.rebootAllPhonesSuccess(action);
                        }),
                        catchError((error) => {
                           return of(AccountDetailActions.rebootAllPhonesFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   rebootAllPhonesSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AccountDetailActions.rebootAllPhonesSuccess),
            tap((action) => {
               this.snackBar.open('Reboot Initiated for all Phones');
            })
         );
      },
      { dispatch: false }
   );

   dialogId: string;

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private store: Store<AppState>,
      private accountDetailApiService: AccountDetailApiService,
      private confirmationService: ConfirmationDialogService,
      private snackBar: MatSnackBar,
      private router: Router,
      private location: Location,
      private dialogService: LcsDialogService,
      private dialog: MatDialog
   ) {}
}
